import { NewAddressModel, UpdateAddressModel } from "services/api/cplapi/CPLAPI";
import type { AddressFormValues } from "../schemas/addressSchema";

export const MAX_ADDRESS_LINES = 5;
export const UK_COUNTRY_ISO_CODE = "GB";

const isEmptyLine = (line: string) => line.trim() === "";

export const addressFieldPaths = (name: string): string[] => [
  ...Array.from({ length: MAX_ADDRESS_LINES }, (_, index) => `${name}.addressLines[${index}]`),
  `${name}.city`,
  `${name}.county`,
  `${name}.countryCodeID`,
  `${name}.postCode`,
];

export const toAddressLinePath = (path: string): string =>
  path.replace(
    /(^|\.)addressLine(\d+)$/,
    (_match, prefix, lineNumber) => `${prefix}addressLines[${Number(lineNumber) - 1}]`
  );

export const visibleLineCount = (lines: string[]): number => {
  const lastFilledIndex = lines.reduce((last, line, index) => (isEmptyLine(line) ? last : index), -1);
  return Math.min(MAX_ADDRESS_LINES, lastFilledIndex + 2);
};

export const normalizePostcode = (postCode: string, countryIsoCode?: string): string =>
  countryIsoCode === UK_COUNTRY_ISO_CODE ? postCode.trim().toUpperCase() : postCode;

const toOptional = (value: string) => value.trim() || undefined;

export const toNewAddressModel = (address: AddressFormValues): NewAddressModel => {
  const [addressLine1, addressLine2, addressLine3, addressLine4, addressLine5] = address.addressLines.map(toOptional);

  return new NewAddressModel({
    addressLine1,
    addressLine2,
    addressLine3,
    addressLine4,
    addressLine5,
    city: toOptional(address.city),
    county: toOptional(address.county),
    postCode: normalizePostcode(address.postCode, address.countryIsoCode),
    countryCodeID: address.countryCodeID,
  });
};

export const toUpdateAddressModel = (addressID: string, address: AddressFormValues): UpdateAddressModel =>
  new UpdateAddressModel({ ...toNewAddressModel(address), addressID });
